import { useGetGrowthbookVariantData } from 'nove8/hooks/useGetGrowthbookVariantData'
import { useGetRemoteConfigVariantData } from 'nove8/hooks/useGetRemoteConfigVariantData'
import { useInitCookieyesTag } from 'nove8/hooks/useInitCookiyesTag'
import { useInitPinterestTag } from 'nove8/hooks/useInitPinterestTag'
import { DYNAMIC_COMPONENTS_MAP } from 'nove8/constants/dynamic-components-map'
import {
  Cohort,
  COHORTS_REQUIRING_FIREBASE_TOKEN,
} from 'nove8/constants/root-constants'
import { FlowRouterContext } from 'nove8/contexts/flowRouterContext'
import { useCreateXStateMachine } from 'nove8/hooks/useCreateXStateMachine'
import { useEventLoggerInitialization } from 'nove8/hooks/useEventLoggerInitialization'
import { useFacebookPixelInitialization } from 'nove8/hooks/useFacebookPixelInitialization'
import { useGAPageTracker } from 'nove8/hooks/useGAPageTracker'
import { useSendSavedAnswers } from 'nove8/hooks/useSendSavedAnswers'
import { useStartSessionEvent } from 'nove8/hooks/useStartSessionEvent'
import { useTiktokPixelInitialization } from 'nove8/hooks/useTiktokPixelInitialization'
import { useUpdateUrlWithRequiredSearchParams } from 'nove8/hooks/useUpdateUrlWithRequiredSearchParams'
import React, { PropsWithChildren, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { googleAnalyticsLogger } from 'services/googleAnalytics.service'
import { useShallow } from 'zustand/shallow'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandStore } from 'zustandGlobalStore'
import { Spinner } from 'nove8/components/Spinner'

const FallbackComponent: React.FC<PropsWithChildren> = ({ children }) => (
  <div>{children}</div>
)

export const FlowRouter: React.FC = () => {
  const { i18n } = useTranslation()
  const { cohort, leadId, pinterestTagId, currentPageIndex } = useZustandStore(
    useShallow((state) => ({
      cohort: state.lead?.cohort || null,
      leadId: state.lead?.id,
      pinterestTagId: state.lead?.config?.cohort?.pinterestTagId || '',
      currentPageIndex: state.currentPageIndex,
    })),
  )
  const { language } = useZustandSessionStore(
    useShallow((state) => ({
      language: state.language,
    })),
  )
  const urlParams = new URLSearchParams(window.location.search)
  const tokenFromUrl = useMemo(() => urlParams.get('token'), [urlParams])
  const requiresFirebaseToken =
    !!cohort && COHORTS_REQUIRING_FIREBASE_TOKEN.includes(cohort as Cohort)

  const { RoutesList, Header, BgPreloadingMediaProvider } = useMemo(
    () => ({
      RoutesList:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.RoutesList ||
        FallbackComponent,
      Header:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.Header || FallbackComponent,
      BgPreloadingMediaProvider:
        DYNAMIC_COMPONENTS_MAP?.[cohort as Cohort]?.BgPreloadingMediaProvider ||
        FallbackComponent,
    }),
    [cohort],
  )

  const { flowRouterContext, isPageLoaded } = useCreateXStateMachine()

  useInitCookieyesTag() // old subs
  useTiktokPixelInitialization() // new subs
  useFacebookPixelInitialization() // new subs
  useInitPinterestTag(pinterestTagId) // old subs
  useGAPageTracker() // new subs
  useGetRemoteConfigVariantData() // new subs
  useGetGrowthbookVariantData() // new subs
  useEventLoggerInitialization() // new subs
  useStartSessionEvent() // new subs
  useSendSavedAnswers() // new subs
  useUpdateUrlWithRequiredSearchParams() // new subs

  useEffect(() => {
    if (currentPageIndex === 1) {
      window.fbq('track', 'CustomizeProduct', {}, { eventID: leadId })
      googleAnalyticsLogger.logFirstPageCompleted()
    }
  }, [currentPageIndex, leadId])

  useEffect(() => {
    i18n.changeLanguage(language)
  }, [i18n, language])

  if (requiresFirebaseToken && !tokenFromUrl) {
    return <Spinner />
  }

  return (
    <BgPreloadingMediaProvider>
      <FlowRouterContext.Provider value={flowRouterContext}>
        {isPageLoaded && <Header />}
        <RoutesList />
      </FlowRouterContext.Provider>
    </BgPreloadingMediaProvider>
  )
}
